// @flow strict
import React, { useState, type Node } from 'react';
import { useOnVisible } from '@ta/common.is-visible';

/** Component props for a <LazyBackgroundImage> */
type Props = {|
  /**
   * Child nodes to render inside the div
   */
  children?: Node,

  /** The css class name */
  className?: string,

  /** Listener for clicks */
  onClick?: () => void,

  /** The image URL */
  src: string,

  /**
   * A set of source image URLs and their corresponding widths in the format:
   * "small.jpg 100w, medium.jpg 200w, large.jpg 300w"
   *
   * Note: srcSet is currently removed as an attribute for this. That is because inline react styles are done as an object, but
   * doing a srcSet would require using duplicate object keys. A plugin would allow us to circumvent this, but until then we have
   * to remove this option
   */
  // srcSet?: string,

  /**
   * The number of pixels prior to the element coming on screen at which point we want to actually load the
   * image. This applies to both vertical as well as horizontal scrolling. If you are seeing the image popping in
   * as you scroll then you can increase this value to load them more aggressively at the cost of potentially loading
   * images that are never seen.
   */
  threshold?: number,
|};

/**
 * A lazy loaded image. It will render a div tag but withhold any attributes that would cause the actual image to
 * load including 'src' and 'srcset'. It then uses the <HasBeenVisible> component to listen for 'scroll' and 'resize'
 * events and when the element is about to become visible (using the 'threshold' prop as a fuzzy factor') it will
 * re-render and include the remaining attributes.
 */
const LazyBackgroundImage = ({ children, className, onClick, src, threshold = 200 }: Props) => {
  const [hasBeenSeen, setHasBeenSeen] = useState(false);

  const visibilityContainerRef = useOnVisible(() => setHasBeenSeen(true), threshold);

  if (hasBeenSeen === false) {
    return (
      <div className={className} onClick={onClick} ref={visibilityContainerRef}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        backgroundImage: `url(${src})`,
      }}
    >
      {children}
    </div>
  );
};

export default LazyBackgroundImage;
